import React, { Component } from 'react';
import styled, { ThemeProvider, keyframes, DefaultTheme } from 'styled-components';

interface FjProgressBarStyle {
  height?: string;
  width?: string;
  fillColor?: string | string[];
  bgColor?: string;
  borderColor?: string;
  borderRadius?: string;
  progressTextColor?: string;
  progressTextSize?: string;
  progressTextAlign?: string;
  progressTextInset?: string;
}

interface FjProgressBarProps {
  style?: FjProgressBarStyle;
  progressText?: string;
  value: number;
  maxValue: number;
}

interface FjProgressBarState {
  barAbsoluteWidth: number;
}

// The theme shape provided to the styled-components below via ThemeProvider.
// It differs from the app-wide DefaultTheme, so theme access is cast to this.
interface ProgressBarTheme {
  height: string;
  width: string;
  fillColor: string | string[];
  bgColor: string;
  borderColor: string;
  borderRadius: string;
  progressTextSize: string;
  progressTextColor: string;
  progressTextAlign: string;
  progressTextInset: string;
}

class FjProgressBar extends Component<FjProgressBarProps, FjProgressBarState> {
  static defaultProps = {
    style: {},
    progressText: '',
  };

  constructor(props: FjProgressBarProps) {
    super(props);
    this.state = {
      barAbsoluteWidth: 0,
    };
  }

  get styleWithDefaults(): ProgressBarTheme {
    const {
      height,
      width,
      fillColor,
      bgColor,
      borderColor,
      borderRadius,
      progressTextColor,
      progressTextSize,
      progressTextAlign,
      progressTextInset,
    } = this.props.style ?? {};
    return {
      height: height || '20px',
      width: width || '100%',
      fillColor: fillColor || 'currentColor',
      bgColor: bgColor || 'transparent',
      borderColor: borderColor || 'currentColor',
      borderRadius: borderRadius || '0px',
      progressTextSize: progressTextSize || '12px',
      progressTextColor: progressTextColor || 'currentColor',
      progressTextAlign: progressTextAlign || 'left',
      progressTextInset: progressTextInset || '0px',
    };
  }

  setBarAbsoluteWidth(barRef: HTMLDivElement | null): void {
    if (barRef) {
      const { width } = barRef.getBoundingClientRect();
      if (this.state.barAbsoluteWidth !== width) {
        this.setState({
          barAbsoluteWidth: width,
        });
      }
    }
  }

  get barFillPercentage(): number {
    return Math.min(Math.max(100 * (this.props.value / this.props.maxValue), 0), 100);
  }

  get parsedProgressText(): string {
    const PROGRESS_TEXT_TOKENS: Record<string, string | number> = {
      '%v': this.props.value,
      '%m': this.props.maxValue,
      '%p': `${this.barFillPercentage.toFixed(1)}%`,
    };
    let result = this.props.progressText ?? '';
    Object.entries(PROGRESS_TEXT_TOKENS).forEach(([token, value]) => {
      const regex = new RegExp(token, 'g');
      result = result.replace(regex, String(value));
    });
    return result;
  }

  render(): React.ReactNode {
    return (
      <ThemeProvider theme={this.styleWithDefaults as unknown as DefaultTheme}>
        <ProgressBarContainer>
          <Bar ref={(ref) => this.setBarAbsoluteWidth(ref)}>
            <BarFillHandler fillPercentage={this.barFillPercentage}>
              <BarFill absoluteWidth={this.state.barAbsoluteWidth} />
            </BarFillHandler>
          </Bar>
          <ProgressText>{this.parsedProgressText}</ProgressText>
        </ProgressBarContainer>
      </ThemeProvider>
    );
  }
}

export default FjProgressBar;

const asProgressBarTheme = (theme: DefaultTheme): ProgressBarTheme =>
  theme as unknown as ProgressBarTheme;

const ProgressBarContainer = styled.div`
  width: ${(props) => asProgressBarTheme(props.theme).width};
`;

const Bar = styled.div`
  display: block;
  position: relative;
  width: 100%;
  height: ${(props) => asProgressBarTheme(props.theme).height};
  background-color: ${(props) => asProgressBarTheme(props.theme).bgColor};
  border: 1px solid ${(props) => asProgressBarTheme(props.theme).borderColor};
  border-radius: ${(props) => asProgressBarTheme(props.theme).borderRadius};
  overflow: hidden;
`;

const fillBar = (props: { fillPercentage: number }) => keyframes`
  from {
    width: 0px;
  }
  to {
    width: ${props.fillPercentage}%;
  }
`;

const BarFillHandler = styled.div<{ fillPercentage: number }>`
  position: absolute;
  top: 0px;
  left: 0px;
  bottom: 0px;
  overflow: hidden;
  animation-name: ${(props) => fillBar(props)};
  animation-duration: 0.5s;
  animation-timing-function: ease-in-out;
  animation-fill-mode: forwards;
`;

const getFillColor = (fillColor: string | string[]): string => {
  if (Array.isArray(fillColor)) {
    if (fillColor.length === 0) {
      throw new Error("Prop 'fillColor' must not be an empty array");
    }
    if (fillColor.length === 1) {
      return fillColor[0];
    }
    return `linear-gradient(to right, ${fillColor.join(', ')})`;
  }
  return fillColor;
};

const BarFill = styled.div<{ absoluteWidth: number }>`
  position: absolute;
  top: 0px;
  left: 0px;
  bottom: 0px;
  width: ${(props) => props.absoluteWidth}px;
  background: ${(props) => getFillColor(asProgressBarTheme(props.theme).fillColor)};
`;

const ProgressText = styled.div`
  color: ${(props) => asProgressBarTheme(props.theme).progressTextColor};
  font-size: ${(props) => asProgressBarTheme(props.theme).progressTextSize};
  width: 100%;
  text-align: ${(props) => asProgressBarTheme(props.theme).progressTextAlign};
  padding: 2px ${(props) => asProgressBarTheme(props.theme).progressTextInset};
`;
